import {
    Component,
    computed,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    FormControl,
    FormGroup,
    Validators,
    ReactiveFormsModule,
} from '@angular/forms';
import { Client } from 'src/app/models/client.model';
import { Project } from 'src/app/models/project-model';
import { WorkOrder } from 'src/app/models/work-order.model';
import { WorkOrderService } from 'src/app/services/work-order.service';
import { PastDateValidator } from 'src/app/validators/past-date.validator';
import { ClientService } from 'src/app/services/client.service';
import { Page } from 'src/app/models/page.model';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { AddClientComponent } from '../../clients/add-client/add-client.component';
import { AddProjectComponent } from '../../projects/add-project/add-project.component';
import { ProjectsService } from 'src/app/services/projects.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatOptionModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgIf, NgFor, DatePipe } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { VisibleDirective } from '../../../directives/visible.directive';
import { WorkOrderNote } from 'src/app/models/work-order-notes';
import { WorkOrderNoteService } from 'src/app/services/work-order-note.service';
import { AppStateService } from 'src/app/services/app-state-service';
import { MatCardModule } from '@angular/material/card';
import { UniversalSnackbarService } from 'src/app/services/universal-snackbar.service';
import { SnackbarMessages as MSG } from 'src/app/enums/snackBar.enum';
import {
    COUNT,
    STATUS,
    WORKORDER,
} from 'src/app/constants/queryParamConstants';
import { COUNT_ALL } from 'src/app/constants/searchConstants';
import { ThemeService } from 'src/app/services/theme.service';
import { EditorModule } from '@tinymce/tinymce-angular';
import {
    DISABLECONTEXTMENU,
    DARKTHEME,
    FONTFAMILYFORMATS,
    PLUGINS,
    SKINURL,
    TOOLBAR,
} from 'src/app/constants/WYSIWYGConstants';
import { User } from 'src/app/models/User';
import { EmployeeTypeService } from 'src/app/services/employee-type.service';
import { EmployeeType } from 'src/app/models/employee-type.model';
import { UserService } from 'src/app/services/user.service';
import { ACTIVE } from 'src/app/constants/statusConstant';
import { PDFMIMETYPE } from 'src/app/constants/exportConstants';

interface IWorkOrderFormControls {
    id: FormControl<string>;
    name: FormControl<string>;
    clientId: FormControl<string>;
    projectId: FormControl<string>;
    contactName: FormControl<string>;
    streetAddress: FormControl<string>;
    location: FormControl<string>;
    phone: FormControl<string>;
    email: FormControl<string>;
    consultantName: FormControl<string>;
    consultantRate: FormControl<string>;
    consultantPosition: FormControl<string>;
    billableExpense: FormControl<string>;
    startDate: FormControl<Date>;
    endDate: FormControl<Date>;
    completedDate: FormControl<Date>;
    createDate: FormControl<Date>;
    signed: FormControl<boolean>;
    active: FormControl<boolean>;
    notesSelection: FormControl<string>;
    notes: FormControl<string>;
}

@Component({
    selector: 'app-open-work-order',
    templateUrl: './open-work-order.component.html',
    styleUrls: ['./open-work-order.component.scss'],
    imports: [
        NgIf,
        MatProgressBarModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatTooltipModule,
        MatSelectModule,
        MatOptionModule,
        NgFor,
        MatDatepickerModule,
        DatePipe,
        MatCheckboxModule,
        VisibleDirective,
        MatCardModule,
        EditorModule,
    ],
})
export class OpenWorkOrderComponent implements OnInit {
    workOrder: WorkOrder;
    clients: Client[];
    projects: Project[];
    filteredProjects: Project[];
    workOrderNotes: WorkOrderNote[];
    loading: boolean;
    creating: boolean;
    pdfLoading: boolean;
    mode: string;
    activeUsers: WritableSignal<User[]> = signal([]);
    employeeTypes: WritableSignal<EmployeeType[]> = signal([]);
    projectLeads: Signal<User[]> = computed(() =>
        this.activeUsers().filter((contact: User) =>
            this.employeeTypes().find((employee) => {
                return (
                    employee.id == contact.employeeTypeId &&
                    employee.projectLead
                );
            }),
        ),
    );

    address = {
        streetAddress: '',
        location: '',
    };

    checkForm = new FormGroup({
        startDateTBD: new FormControl(false),
        endDateTBD: new FormControl(false),
        populateNames: new FormControl(false),
    });

    workOrderForm: FormGroup<IWorkOrderFormControls> = new FormGroup(
        {
            id: new FormControl(null),
            name: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(150),
            ]),
            clientId: new FormControl(null, [Validators.required.bind(this)]),
            contactName: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(100),
            ]),
            streetAddress: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(50),
            ]),
            location: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(50),
            ]),
            phone: new FormControl(null, [Validators.maxLength(50)]),
            email: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(150),
            ]),
            consultantName: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(100),
            ]),
            consultantPosition: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(100),
            ]),
            consultantRate: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(50),
            ]),
            billableExpense: new FormControl(null, [
                Validators.required.bind(this),
                Validators.maxLength(50),
            ]),
            startDate: new FormControl(null),
            endDate: new FormControl(null),
            projectId: new FormControl(null, [Validators.required.bind(this)]),
            completedDate: new FormControl(null),
            createDate: new FormControl(null),
            signed: new FormControl(null),
            active: new FormControl(null),
            notesSelection: new FormControl(null),
            notes: new FormControl(null),
        },
        [PastDateValidator],
    );

    constructor(
        private route: ActivatedRoute,
        private workOrderService: WorkOrderService,
        private clientService: ClientService,
        private projectsService: ProjectsService,
        private dialogService: MatDialog,
        private navigationService: NavigationService,
        private dialog: MatDialog,
        private snackBar: UniversalSnackbarService,
        private router: Router,
        private workOrderNoteService: WorkOrderNoteService,
        private appStateService: AppStateService,
        private themeService: ThemeService,
        private employeeTypeService: EmployeeTypeService,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            if (params['workOrderId']) {
                this.loading = true;
                this.workOrderService
                    .get([params['workOrderId']])
                    .subscribe((response: WorkOrder) => {
                        this.workOrder = response;
                        this.address = JSON.parse(this.workOrder.address);

                        this.checkCreating(params['creating'] === 'true');
                        this.initializeForm();

                        if (this.workOrder.signed) {
                            this.workOrderForm.disable();
                        }

                        if (!this.workOrder.active) {
                            this.workOrderForm.controls.active.enable();
                        }

                        if (!this.workOrder.startDate) {
                            this.checkForm.controls.startDateTBD.setValue(true);
                        }

                        if (!this.workOrder.endDate) {
                            this.checkForm.controls.endDateTBD.setValue(true);
                        }

                        this.loading = false;
                    });
            } else {
                this.workOrder = this.buildWorkOrder();
                this.checkCreating(params['creating'] === 'true');
                this.initializeForm();
            }
        });

        this.checkForm.controls.startDateTBD.valueChanges.subscribe((value) => {
            if (value) {
                this.workOrderForm.controls.startDate.clearValidators();
                this.workOrderForm.controls.startDate.updateValueAndValidity();
            } else {
                this.workOrderForm.controls.startDate.setValidators([
                    Validators.required,
                    PastDateValidator,
                ]);
                this.workOrderForm.controls.startDate.updateValueAndValidity();
            }
        });

        this.checkForm.controls.endDateTBD.valueChanges.subscribe((value) => {
            if (value) {
                this.workOrderForm.controls.endDate.setValue(null);
                this.workOrderForm.controls.endDate.clearValidators();
                this.workOrderForm.get('endDate').updateValueAndValidity();
            } else {
                this.workOrderForm.controls.endDate.setValidators([
                    Validators.required,
                    PastDateValidator,
                ]);
                this.workOrderForm.controls.endDate.updateValueAndValidity();
            }
        });

        this.getClients();
        this.getProjects();
        this.getWorkOrderNotes();
        this.getActiveUsers();
    }

    checkCreating(creating: boolean) {
        this.creating = creating;

        if (this.creating) {
            this.workOrder.active = false;
            this.workOrder.signed = false;
            this.workOrder.completedDate = null;
            this.workOrder.createDate = null;
        }
    }

    onClientSelectionChange(event: MatSelectChange): void {
        if (event.value === '') {
            const map = new Map();
            map.set(COUNT, COUNT_ALL);

            this.clientService
                .get([], map)
                .subscribe((response: Page<Client>) => {
                    const dialogRef = this.dialogService.open(
                        AddClientComponent,
                        { disableClose: true, data: response.items },
                    );

                    dialogRef.afterClosed().subscribe((dialogResult) => {
                        if (dialogResult.success) {
                            this.getClients();
                            this.workOrderForm.controls.clientId.setValue(
                                dialogResult.clientId.id,
                            );
                        }
                    });
                });
        }

        this.filterProjects();

        const associatedProject = this.filteredProjects.find(
            (project) =>
                project.clientId === this.workOrderForm.controls.clientId.value,
        );

        if (associatedProject) {
            this.workOrderForm.controls.projectId.setValue(
                associatedProject.id,
            );
        } else {
            this.workOrderForm.controls.projectId.setValue(null);
        }
    }

    filterProjects(): void {
        this.filteredProjects = this.projects.filter(
            (p) => p.clientId == this.workOrderForm.controls.clientId.value,
        );
    }

    onProjectSelectionChange(event: MatSelectChange): void {
        if (event.value === '') {
            const map = new Map();
            map.set(COUNT, COUNT_ALL);

            this.clientService.get([], map).subscribe(() => {
                const dialogRef = this.dialogService.open(AddProjectComponent, {
                    disableClose: true,
                    data: {
                        clientId: this.workOrderForm.controls.clientId.value,
                        projectLeads: this.projectLeads,
                        activeUsers: this.activeUsers,
                    },
                });

                dialogRef.afterClosed().subscribe((dialogResult) => {
                    if (dialogResult.success) {
                        this.getProjects();
                        this.workOrderForm.controls.projectId.setValue(
                            dialogResult.projectId.id,
                        );
                    }
                });
            });
        }
    }

    close(): void {
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
            data: 'Are you sure you want to close this work order? \nWarning: All unsaved changes will be lost.',
        });

        dialog.afterClosed().subscribe((response: boolean) => {
            if (response === true) {
                this.navigationService.Route('workorders');
            }
        });
    }

    save(): boolean {
        this.workOrderForm.updateValueAndValidity();

        if (
            this.workOrderForm.valid ||
            this.workOrderForm.controls.active.disabled
        ) {
            this.loading = true;
            this.address.location = this.workOrderForm.controls.location.value;
            this.address.streetAddress =
                this.workOrderForm.controls.streetAddress.value;

            const workOrder = this.buildWorkOrder();
            workOrder.consultantRate = workOrder.consultantRate.toString();
            workOrder.billableExpense = workOrder.billableExpense.toString();
            workOrder.address = JSON.stringify(this.address);

            if (this.creating) {
                workOrder.id = null;
                workOrder.active = false;

                this.workOrderService.post([], workOrder).subscribe({
                    next: (response) => {
                        this.workOrderForm.patchValue(response);

                        this.workOrderForm.controls.createDate.setValue(
                            new Date(),
                        );
                        this.loading = false;
                        this.creating = false;
                        this.updateUrl();
                        this.snackBar.open(MSG.WORK_ORDER_CREATED);
                    },
                    error: (error) => {
                        this.snackBar.open(MSG.WORK_ORDER_CREATED_F);
                        this.loading = false;
                    },
                });
            } else {
                workOrder.createDate =
                    this.workOrderForm.controls.createDate.value;

                this.workOrderService.put([workOrder.id], workOrder).subscribe({
                    next: () => {
                        this.snackBar.open(MSG.WORK_ORDER_UPDATED);
                        this.loading = false;
                    },
                    error: () => {
                        this.loading = false;
                        this.snackBar.open(MSG.WORK_ORDER_UPDATED_F);
                    },
                });
            }

            return true;
        } else {
            Object.values(this.workOrderForm.controls).forEach((control) => {
                control.markAsTouched();
            });

            return false;
        }
    }

    initializeForm(workOrder?: WorkOrder): void {
        if (workOrder) {
            this.workOrderForm.patchValue(workOrder);
            this.workOrderForm.controls.streetAddress.setValue(
                this.address.streetAddress,
            );
            this.workOrderForm.controls.location.setValue(
                this.address.location,
            );
        } else if (this.workOrder) {
            this.workOrderForm.controls.streetAddress.setValue(
                this.address.streetAddress,
            );
            this.workOrderForm.controls.location.setValue(
                this.address.location,
            );
            this.workOrderForm.patchValue(this.workOrder);
        } else {
            this.workOrderForm.setValue({
                id: null,
                name: null,
                clientId: null,
                contactName: null,
                streetAddress: null,
                location: null,
                phone: null,
                email: null,
                consultantName: null,
                consultantPosition: null,
                consultantRate: null,
                billableExpense: null,
                startDate: null,
                endDate: null,
                projectId: null,
                completedDate: null,
                signed: false,
                active: false,
                createDate: null,
                notesSelection: null,
                notes: null,
            });
        }

        if (this.creating) {
            this.workOrderForm.controls.id.setValue('');
        }
    }

    buildWorkOrder(): WorkOrder {
        const workOrder: WorkOrder = {
            id: this.workOrderForm.controls.id.value,
            name: this.workOrderForm.controls.name.value,
            clientId: this.workOrderForm.controls.clientId.value,
            contactName: this.workOrderForm.controls.contactName.value,
            address: JSON.stringify(this.address),
            phone: this.workOrderForm.controls.phone.value,
            email: this.workOrderForm.controls.email.value,
            consultantName: this.workOrderForm.controls.consultantName.value,
            consultantPosition:
                this.workOrderForm.controls.consultantPosition.value,
            consultantRate: this.workOrderForm.controls.consultantRate.value,
            billableExpense: this.workOrderForm.controls.billableExpense.value,
            startDate: this.workOrderForm.controls.startDate.value,
            endDate: this.workOrderForm.controls.endDate.value,
            projectId: this.workOrderForm.controls.projectId.value,
            completedDate: this.workOrderForm.controls.completedDate.value,
            signed: this.workOrderForm.controls.signed.value,
            active: this.workOrderForm.controls.active.value,
            notes: this.workOrderForm.controls.notes.value,
            userName: null,
        };

        if (this.checkForm.controls.startDateTBD.value) {
            workOrder.startDate = null;
        }

        if (this.checkForm.controls.endDateTBD.value) {
            workOrder.endDate = null;
        }

        if (this.checkForm.controls.populateNames.value) {
            workOrder.userName =
                this.appStateService.me$.value.firstName +
                ' ' +
                this.appStateService.me$.value.lastName;
        }

        return workOrder;
    }

    export() {
        if (this.save()) {
            this.pdfLoading = true;
            const workOrder = this.buildWorkOrder();
            workOrder.consultantRate = workOrder.consultantRate.toString();
            workOrder.billableExpense = workOrder.billableExpense.toString();

            if (this.creating) {
                workOrder.id = null;
                workOrder.createDate = new Date();
            } else {
                workOrder.createDate =
                    this.workOrderForm.controls.createDate.value;
            }

            var queryParams = new Map<string, any>();
            queryParams.set(WORKORDER, JSON.stringify(workOrder));

            this.workOrderService
                .exportReport(['export'], workOrder)
                .subscribe({
                    next: (buffer: Blob) => {
                        let mimeType: string = PDFMIMETYPE;

                        const data: Blob = new Blob([buffer], {
                            type: mimeType,
                        });

                        const url = window.URL.createObjectURL(data);

                        window.open(url, '_blank');

                        this.pdfLoading = false;
                    },
                    error: () => {
                        this.pdfLoading = false;
                        this.snackBar.open(MSG.FILE_EXPORT_F);
                    },
                });
        }
    }

    sign() {
        const dialog = this.dialog.open(ConfirmationDialogComponent, {
            data: 'Are you sure you want to sign this work order? \nWarning: A signed work order is uneditable.',
        });

        dialog.afterClosed().subscribe((response: boolean) => {
            if (response === true) {
                this.workOrderForm.controls.signed.setValue(true);
                this.workOrderForm.controls.completedDate.setValue(new Date());

                if (this.save() && !this.workOrderForm.controls.active.value) {
                    this.workOrderForm.disable();
                    this.workOrderForm.controls.active.enable();
                } else if (this.save()) {
                    this.workOrderForm.disable();
                } else {
                    this.workOrderForm.controls.signed.setValue(false);
                    this.workOrderForm.controls.completedDate.setValue(null);
                }
            } else {
                this.workOrderForm.controls.signed.setValue(false);
            }
        });
    }

    activeChange() {
        this.workOrderForm.updateValueAndValidity();

        if (this.workOrderForm.valid) {
            this.workOrderForm.controls.active.setValue(
                this.workOrderForm.controls.active.value,
            );

            if (this.workOrderForm.controls.signed.value) {
                this.workOrderForm.disable();
            }
        } else {
            this.workOrderForm.controls.active.setValue(
                !this.workOrderForm.controls.active.value,
            );
        }
    }

    private getClients(): void {
        var queryParams = new Map<string, any>();
        queryParams.set(COUNT, COUNT_ALL);

        this.clientService
            .get([], queryParams)
            .subscribe((response: Page<Client>) => {
                this.clients = response.items;
            });
    }

    private getProjects(): void {
        var queryParams = new Map<string, any>();
        queryParams.set(COUNT, COUNT_ALL);

        this.projectsService
            .get([], queryParams)
            .subscribe((response: Page<Project>) => {
                this.projects = response.items;
                this.filterProjects();
            });
    }

    getActiveUsers(): void {
        this.loading = true;
        this.employeeTypeService
            .get([], new Map<string, string | number>())
            .subscribe({
                next: (employeeType: Page<EmployeeType>) => {
                    this.employeeTypes.set(employeeType.items);
                    this.userService
                        .get(
                            [],
                            new Map<string, unknown>([
                                [STATUS, ACTIVE],
                                [COUNT, COUNT_ALL],
                            ]),
                        )
                        .subscribe({
                            next: (users: Page<User>) => {
                                this.activeUsers.set(users.items);
                                this.loading = false;
                            },
                            error: () => {
                                this.snackBar.open(MSG.DATA_F);
                                this.loading = false;
                            },
                        });
                },
                error: () => {
                    this.snackBar.open(MSG.DATA_F);
                    this.loading = false;
                },
            });
    }

    updateUrl(): void {
        void this.router.navigate([]);

        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                workOrderId: this.workOrderForm.controls.id.value,
                creating: this.creating,
            },
        });
    }

    getWorkOrderNotes(): void {
        this.workOrderNoteService
            .get([])
            .subscribe((response: WorkOrderNote[]) => {
                this.workOrderNotes = response;
            });
    }

    onNoteSelectionChange(event): void {
        this.workOrderForm.controls.notes.setValue(event.value.body);
    }

    protected get editorInit() {
        this.mode = this.themeService.getCurrentTheme();
        if (this.mode.toLowerCase() == DARKTHEME) {
            return {
                skin_url: SKINURL,
                plugins: PLUGINS,
                toolbar: TOOLBAR,
                font_family_formats: FONTFAMILYFORMATS,
                color_map: [],
                contextmenu: DISABLECONTEXTMENU,
            };
        } else {
            return {
                plugins: PLUGINS,
                toolbar: TOOLBAR,
                font_family_formats: FONTFAMILYFORMATS,
                color_map: [],
                contextmenu: DISABLECONTEXTMENU,
            };
        }
    }
}
